import React, { useRef, useState } from 'react';
import { X, Upload, Loader2, FileDown, CheckCircle2, AlertTriangle, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { suppliersAPI } from '../services/api';

const FIELD_LABEL = { ref: 'Référence fabricant', supplier_ref: 'Réf. fournisseur', purchase_price: "Prix d'achat HT", stock: 'Stock fournisseur', lead_time_days: 'Délai', sale_price: 'Prix de vente TTC' };
const money = (v) => (v == null ? '—' : `${Number(v).toFixed(2)} €`);

// Admin: import a supplier price list (CSV / XLSX) → preview → apply
const PriceListImportModal = ({ supplier, onClose, onApplied }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [updateSale, setUpdateSale] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const analyse = async (f) => {
    setFile(f); setPreview(null); setResult(null); setError('');
    if (!f) return;
    setBusy(true);
    try {
      setPreview(await suppliersAPI.importPreview(supplier.id, f));
    } catch (e) {
      setError(e.response?.data?.detail || 'Fichier illisible');
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    setBusy(true);
    try {
      const rows = preview.matched.map(m => ({ product_id: m.product_id, supplier_ref: m.supplier_ref || '', purchase_price: m.purchase_price, stock: m.stock, lead_time_days: m.lead_time_days, sale_price: m.sale_price }));
      const r = await suppliersAPI.importApply(supplier.id, rows, updateSale);
      setResult(r);
      toast.success(r.message);
      onApplied && onApplied(r);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import impossible');
    } finally {
      setBusy(false);
    }
  };

  const hasSale = preview?.mapping?.sale_price;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden" data-testid="price-list-import-modal">
        <div className="p-5 border-b bg-gradient-to-r from-[#1e3a5f] to-[#2d4a6f] text-white flex items-center justify-between">
          <h2 className="text-lg font-bold flex items-center gap-2"><Upload size={20} /> Importer un tarif — {supplier.name}</h2>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="import-close"><X size={22} /></button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {!result && (
            <div className="flex flex-wrap items-center gap-3 bg-gray-50 border rounded-lg p-4">
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm,text/csv" className="hidden" data-testid="import-file-input" onChange={(e) => analyse(e.target.files?.[0] || null)} />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="import-choose-file"><Upload size={16} className="mr-2" /> {file ? file.name : 'Choisir un fichier CSV ou Excel'}</Button>
              <button type="button" data-testid="import-template" onClick={() => suppliersAPI.importTemplate()} className="text-sm text-blue-700 hover:underline inline-flex items-center gap-1"><FileDown size={14} /> Télécharger le modèle CSV</button>
              <p className="text-xs text-gray-500 w-full">Colonnes reconnues automatiquement : Référence (fabricant/OEM) ou Réf fournisseur pour la correspondance, Prix achat HT, Stock, Délai, Prix vente TTC (optionnel). Séparateur ; ou , — décimales avec virgule acceptées.</p>
            </div>
          )}
          {busy && !preview && !result && <div className="flex justify-center py-6"><Loader2 className="animate-spin text-orange-500" /></div>}
          {error && <p className="text-sm text-red-600 flex items-center gap-2" data-testid="import-error"><AlertTriangle size={16} /> {error}</p>}

          {preview && !result && (
            <>
              <div className="flex flex-wrap gap-2 text-xs" data-testid="import-mapping">
                {Object.entries(preview.mapping).map(([k, v]) => <span key={k} className="px-2 py-1 rounded-full bg-blue-50 text-blue-800 border border-blue-200">{FIELD_LABEL[k]} ← « {v} »</span>)}
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3"><p className="text-2xl font-bold text-green-700" data-testid="import-matched-count">{preview.matched.length}</p><p className="text-xs text-green-800">lignes reconnues</p></div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3"><p className="text-2xl font-bold text-blue-700" data-testid="import-new-links">{preview.new_links}</p><p className="text-xs text-blue-800">nouveaux produits liés</p></div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3"><p className="text-2xl font-bold text-amber-700" data-testid="import-unmatched-count">{preview.unmatched.length}</p><p className="text-xs text-amber-800">non reconnues (ignorées)</p></div>
              </div>

              {preview.matched.length > 0 && (
                <div className="border rounded-lg overflow-x-auto">
                  <table className="w-full text-sm min-w-[720px]" data-testid="import-preview-table">
                    <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                      <tr><th className="px-3 py-2 text-left">Produit</th><th className="px-3 py-2 text-left">Réf. fourn.</th><th className="px-3 py-2 text-right">PA HT</th><th className="px-3 py-2 text-right">Stock fourn.</th>{hasSale && <th className="px-3 py-2 text-right">PV TTC</th>}</tr>
                    </thead>
                    <tbody className="divide-y">
                      {preview.matched.map(m => (
                        <tr key={m.product_id} data-testid={`import-row-${m.product_id}`}>
                          <td className="px-3 py-2"><div className="flex items-center gap-2"><img src={m.image} alt="" className="w-8 h-8 object-contain bg-gray-50 rounded" /><div><p className="font-medium text-gray-900">{m.name} {!m.linked && <span className="ml-1 text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-1.5 rounded-full inline-flex items-center gap-0.5"><Link2 size={9} /> nouveau lien</span>}</p><p className="text-xs text-gray-400">{m.brand} · Réf. {m.product_ref} · ligne {m.line}</p></div></div></td>
                          <td className="px-3 py-2 font-mono text-xs">{m.supplier_ref || '—'}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">{m.purchase_price == null ? <span className="text-gray-400">inchangé</span> : <><span className="text-gray-400 line-through mr-1">{money(m.old_purchase_price)}</span><strong>{money(m.purchase_price)}</strong></>}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">{m.stock == null ? <span className="text-gray-400">inchangé</span> : <><span className="text-gray-400 line-through mr-1">{m.old_stock ?? '—'}</span><strong>{m.stock}</strong></>}</td>
                          {hasSale && <td className="px-3 py-2 text-right whitespace-nowrap">{m.sale_price == null ? <span className="text-gray-400">—</span> : <><span className="text-gray-400 line-through mr-1">{money(m.old_sale_price)}</span><strong>{money(m.sale_price)}</strong></>}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {preview.unmatched.length > 0 && (
                <details className="text-sm" data-testid="import-unmatched">
                  <summary className="cursor-pointer text-amber-800">Voir les {preview.unmatched.length} ligne(s) non reconnue(s)</summary>
                  <ul className="mt-2 text-xs text-gray-600 grid grid-cols-2 md:grid-cols-3 gap-1">
                    {preview.unmatched.map(u => <li key={u.line} className="truncate">ligne {u.line} : {u.ref || u.supplier_ref || '(vide)'}{u.reason ? ` — ${u.reason}` : ''}</li>)}
                  </ul>
                </details>
              )}
              {hasSale && (
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" data-testid="import-update-sale" checked={updateSale} onChange={(e) => setUpdateSale(e.target.checked)} className="rounded border-gray-300 text-orange-500" />
                  Mettre aussi à jour le <strong>prix de vente TTC</strong> avec la colonne « {preview.mapping.sale_price} »
                </label>
              )}
            </>
          )}

          {result && (
            <div className="space-y-3" data-testid="import-result">
              <p className="text-green-700 font-medium flex items-center gap-2"><CheckCircle2 size={18} /> {result.message} · {result.new_links} nouveau(x) lien(s){result.sale_price_updated ? ` · ${result.sale_price_updated} prix de vente modifié(s)` : ''}</p>
              {result.below_suggested.length > 0 ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="import-below-suggested">
                  <p className="text-sm font-semibold text-red-700 flex items-center gap-2"><AlertTriangle size={16} /> {result.below_suggested.length} produit(s) désormais sous le prix conseillé</p>
                  <ul className="mt-2 text-sm text-gray-800 space-y-1">
                    {result.below_suggested.map(b => <li key={b.product_id}>{b.name} : vendu {money(b.price)} (marge {b.margin_pct} %) — conseillé <strong>{money(b.suggested_price)}</strong></li>)}
                  </ul>
                  <p className="text-xs text-gray-600 mt-2">Utilisez « Appliquer le prix conseillé » dans l'onglet Produits pour corriger en un clic.</p>
                </div>
              ) : <p className="text-sm text-gray-600">Aucun produit sous le prix conseillé après import.</p>}
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} data-testid="import-cancel">{result ? 'Fermer' : 'Annuler'}</Button>
          {preview && !result && <Button data-testid="import-apply" onClick={apply} disabled={busy || !preview.matched.length} className="bg-green-600 hover:bg-green-700">{busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <CheckCircle2 size={16} className="mr-2" />} Appliquer {preview.matched.length} ligne(s)</Button>}
        </div>
      </div>
    </div>
  );
};

export default PriceListImportModal;
